/**
 * 개발 전용: 산출물 평가 테스트기 (범용 판때기).
 * 계획서(plan) 1~5단계 / 최종, 이후 스크리너/가이드라인/인터뷰보고 확장.
 */
import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { devEvaluate } from "../services/api";
import "./DevEvaluatorPage.scss";

const ARTIFACT_TYPES = [
  { value: "plan", label: "계획서 (Plan)" },
  { value: "survey", label: "스크리너/설문 (Survey)" },
  { value: "guideline", label: "가이드라인 (Guideline)" },
  { value: "report", label: "AI 인터뷰 보고 (Report)" },
];

const STAGES = [
  { value: 1, label: "1단계 (상황/컨텍스트)" },
  { value: 2, label: "2단계 (목적/질문/가설)" },
  { value: 3, label: "3단계 (방법론/설계)" },
  { value: 4, label: "4단계 (대상/스크리너)" },
  { value: 5, label: "5단계 (추가 요구사항)" },
  { value: "final", label: "최종 산출물" },
];

const EVALUATION_TYPES = [
  { value: "plan_quality", label: "산출물 품질 (최종 계획서·문서)" },
  { value: "card_appropriateness", label: "카드 적절성 (1~5단계 추천·선택 카드)" },
];

/** 단계별 카드 적절성 평가 기준 (1~5단계에서 “적절한 카드를 추천했는지”만 평가) */
const STAGE_CARD_CRITERIA = {
  1: [
    {
      id: "context_cards",
      name: "1단계(상황/컨텍스트) 카드 적절성 [100점]",
      max_score: 100,
      sub_items: [
        { id: "fit1", name: "[적합성] 추천·선택된 카드들이 ‘상황/컨텍스트’ 단계 목적(배경·문제 명확화)에 부합하는가?" },
        { id: "spec1", name: "[구체성] 배경·문제를 모호하지 않게 구체적으로 다루는 카드가 포함되었는가?" },
        { id: "scope1", name: "[범위] 문제의 범위, 제약, 프로덕트 단계를 다루는 카드가 있는가?" },
        { id: "gap1", name: "[갭] 이미 아는 것(Known)과 모르는 것(Unknown) 구분·갭을 다루는 카드가 있는가?" },
      ],
    },
  ],
  2: [
    {
      id: "goals_cards",
      name: "2단계(목적/질문/가설) 카드 적절성 [100점]",
      max_score: 100,
      sub_items: [
        { id: "fit2", name: "[적합성] 추천·선택된 카드들이 ‘목적/질문/가설’ 단계 목적에 부합하는가?" },
        { id: "spec2", name: "[구체성] 목표·가설·RQ가 구체적으로 드러나는 카드가 포함되었는가?" },
        { id: "ver2", name: "[검증가능성] 가설이 참/거짓 판별 가능한 형태로 기술된 카드가 있는가?" },
        { id: "rq2", name: "[위계] 핵심 질문과 세부 질문이 위계적으로 정리된 카드가 있는가?" },
      ],
    },
  ],
  3: [
    {
      id: "methodology_cards",
      name: "3단계(방법론/설계) 카드 적절성 [100점]",
      max_score: 100,
      sub_items: [
        { id: "fit3", name: "[적합성] 추천·선택된 카드들이 ‘방법론/설계’ 단계 목적에 부합하는가?" },
        { id: "match3", name: "[매칭] 질문 유형(이유 vs 규모/빈도)에 맞는 방법론(정성 vs 정량) 카드가 있는가?" },
        { id: "task3", name: "[태스크] 자극물·태스크·시나리오를 구체적으로 다루는 카드가 있는가?" },
        { id: "bias3", name: "[바이어스] 질문/태스크 순서·편향 통제를 고려한 카드가 있는가?" },
      ],
    },
  ],
  4: [
    {
      id: "targeting_cards",
      name: "4단계(대상/스크리너) 카드 적절성 [100점]",
      max_score: 100,
      sub_items: [
        { id: "fit4", name: "[적합성] 추천·선택된 카드들이 ‘대상/스크리너’ 단계 목적에 부합하는가?" },
        { id: "behav4", name: "[행동변수] 인구통계를 넘어 행동·경험 기반 조건을 다루는 카드가 있는가?" },
        { id: "inc4", name: "[적격/제외] Inclusion·Exclusion 조건을 명확히 다루는 카드가 있는가?" },
        { id: "n4", name: "[표본수] 목적에 맞는 표본 수·그룹 구분을 다루는 카드가 있는가?" },
      ],
    },
  ],
  5: [
    {
      id: "feasibility_cards",
      name: "5단계(추가 요구사항) 카드 적절성 [100점]",
      max_score: 100,
      sub_items: [
        { id: "fit5", name: "[적합성] 추천·선택된 카드들이 ‘추가 요구사항’ 단계 목적에 부합하는가?" },
        { id: "detail5", name: "[구체화] 시나리오·태스크·관찰 포인트·제약을 구체화하는 카드가 있는가?" },
        { id: "bias5", name: "[편향·한계] 편향 제거·조사 한계를 고려한 카드가 있는가?" },
      ],
    },
  ],
};

/** 단계별로 적용할 평가 항목 id (산출물 품질 모드, 해당 단계에 맞는 기준만 사용) */
const STAGE_CRITERIA_IDS = {
  1: ["context"],
  2: ["goals"],
  3: ["methodology"],
  4: ["targeting"],
  5: ["feasibility", "actionability"],
  final: null,
};

const DEFAULT_CRITERIA_JSON = `[
  {
    "id": "context",
    "name": "1. 문제 정의 및 배경의 구체성 (Specificity of Context) [20점] 모호한 '현상'을 '해결 가능한 문제'로 좁혔는가?",
    "max_score": 20,
    "sub_items": [
      { "id": "bg1", "name": "[배경 1] 현황 데이터 제시: 리서치를 촉발한 구체적인 데이터(예: 이탈률 15% 증가, VOC 30건 접수)나 관찰된 현상이 명시되었는가? (추상적인 사용성 개선은 감점)" },
      { "id": "bg2", "name": "[배경 2] 비즈니스 임팩트: 이 문제를 해결했을 때 기대되는 비즈니스적 가치(매출, 리텐션, 운영 효율 등)가 연결되어 있는가?" },
      { "id": "bg3", "name": "[배경 3] 프로덕트 단계 인식: 현재 제품 단계(아이디어/프로토타입/라이브)에 맞춰 조사의 제약 사항과 범위가 설정되었는가?" },
      { "id": "bg4", "name": "[배경 4] 문제의 범위 한정: 앱 전체가 아닌 특정 플로우나 특정 기능으로 문제의 범위가 좁혀져 있는가?" },
      { "id": "bg5", "name": "[배경 5] 기존 지식 확인: 이미 알고 있는 사실(Known)과 모르고 있는 사실(Unknown)을 구분하여, 불필요한 중복 조사를 방지했는가?" }
    ]
  },
  {
    "id": "goals",
    "name": "2. 목표 및 가설의 논리성 (Logical Alignment of Goals) [20점] 목표가 문제를 해결하는 열쇠가 되는가?",
    "max_score": 20,
    "sub_items": [
      { "id": "goal1", "name": "[목표 1] 문제-목표 일치: 조사 목표를 달성하면, 1번 항목에서 정의한 문제가 해결되거나 의사결정을 내릴 수 있는가?" },
      { "id": "goal2", "name": "[목표 2] 결과물의 구체화: 목표가 사용자 이해 같은 모호한 문장이 아니라, A와 B안 중 선호도 결정, 이탈 원인 3가지 규명 등 구체적 산출 형태로 정의되었는가?" },
      { "id": "hyp1", "name": "[가설 1] 가설의 검증 가능성: 설정된 가설이 참/거짓(True/False)을 판별할 수 있는 문장으로 기술되었는가?" },
      { "id": "hyp2", "name": "[가설 2] 가설의 근거: 가설이 막연한 추측이 아니라, 행동 데이터나 선행 리서치 등 합리적 근거에 기반했는가?" },
      { "id": "rq1", "name": "[질문 1] 리서치 질문(RQ)의 위계: 핵심 질문(Key Question)과 이를 파악하기 위한 세부 질문(Sub Question)이 위계적으로 구조화되었는가?" }
    ]
  },
  {
    "id": "methodology",
    "name": "3. 방법론 및 조사 설계의 타당성 (Validity of Methodology) [20점] 그 질문에 답하기 위해 가장 적절한 도구인가?",
    "max_score": 20,
    "sub_items": [
      { "id": "m1", "name": "[방법 1] Why/How 매칭: 이유를 묻는다면 정성조사(IDI/FGI), 규모/빈도를 묻는다면 정량조사(설문/로그)로 방법론이 올바르게 매칭되었는가?" },
      { "id": "m2", "name": "[방법 2] 자극물(Stimuli) 정의: 조사 시 사용자에게 보여줄 구체적인 화면(Low-fi/High-fi/경쟁사 앱)이나 상황이 정의되었는가?" },
      { "id": "m3", "name": "[내용 1] 태도 vs 행동 구분: 사용자의 말(Opinion)을 믿을 것인지, 행동(Behavior)을 관찰할 것인지 조사 성격에 맞게 설계되었는가? (UT는 행동 중심이어야 함)" },
      { "id": "m4", "name": "[내용 2] 태스크(Task)의 현실성: (UT/관찰 조사 시) 가입하세요 같은 지시어가 아니라, 친구에게 줄 선물을 골라보세요 같은 현실적 시나리오(Scenario)가 부여되었는가?" },
      { "id": "m5", "name": "[내용 3] 바이어스(Bias) 통제: 질문이나 태스크 순서가 사용자의 답변에 영향을 주지 않도록 설계되었는가? (예: 긍정적 질문 먼저 하지 않기)" }
    ]
  },
  {
    "id": "targeting",
    "name": "4. 대상자 선정 및 스크리닝 정교성 (Targeting Precision) [20점] 진짜 데이터를 줄 수 있는 사람을 뽑는가?",
    "max_score": 20,
    "sub_items": [
      { "id": "t1", "name": "[대상 1] 행동 변수(Behavioral Variable): 단순 인구통계(성별/나이)를 넘어, 서비스 사용 경험/빈도/라이프스타일 등 행동 기반 조건이 포함되었는가?" },
      { "id": "t2", "name": "[대상 2] 극단 유저(Extreme User) 고려: 평균적인 사용자 외에, 헤비 유저나 아예 사용하지 않는 유저 등 인사이트가 풍부한 대상을 고려했는가?" },
      { "id": "t3", "name": "[스크리닝 1] 적격 조건(Inclusion): 리서치 주제에 대해 충분히 말할 수 있는 경험을 가진 사람인지 확인하는 질문이 있는가?" },
      { "id": "t4", "name": "[스크리닝 2] 제외 조건(Exclusion): 데이터의 품질을 해치는 대상(경쟁사 직원, 최근 리서치 참여자, 해당 경험이 전무한 자)을 걸러내는 기준이 명확한가?" },
      { "id": "t5", "name": "[규모] 표본 수의 적절성: 정성조사(5~8명 내외)와 정량조사(통계적 유의성 고려)의 목적에 맞는 N수가 설정되었는가?" }
    ]
  },
  {
    "id": "feasibility",
    "name": "5. 실행 가능성 및 리소스 최적화 (Feasibility & Operations) [10점] 현실적으로 수행 가능한 계획인가?",
    "max_score": 10,
    "sub_items": [
      { "id": "f1", "name": "[일정] 분석 시간 확보: 조사를 수행하는 시간뿐만 아니라, 결과를 분석하고 인사이트를 도출하는 시간(Debriefing)이 일정에 포함되었는가?" },
      { "id": "f2", "name": "[도구] 툴 적합성: 리모트/대면 환경에 따라 녹화, 화면 공유, 기록을 위한 적절한 툴이 선정되었는가?" },
      { "id": "f3", "name": "[역할] R&R 명시: 진행자(Moderator) 외에 기록자(Note-taker)나 관찰자(Observer)의 역할이 계획되어 있는가?" },
      { "id": "f4", "name": "[윤리] 연구 윤리 준수: 참여 동의(Consent), 개인정보 파기 시점, 보상 지급 기준이 명시되었는가?" },
      { "id": "f5", "name": "[파일럿] 예비 조사 계획: 본 조사 전 질문지나 프로토타입의 오류를 점검할 파일럿 테스트(Pilot Test) 계획이 있는가?" }
    ]
  },
  {
    "id": "actionability",
    "name": "6. 산출물 및 활용성 (Actionability) [10점] 결과가 책상 속에 처박히지 않고 쓰일 수 있는가?",
    "max_score": 10,
    "sub_items": [
      { "id": "a1", "name": "[형식] 수용자 맞춤형: 결과를 보는 대상(경영진/디자이너/개발자)이 이해하기 쉬운 형태로 산출물이 정의되었는가?" },
      { "id": "a2", "name": "[연계] Next Step 명시: 조사 결과가 나오면 그 다음 어떤 액션(기획 수정/디자인 반영/백로그 등록)으로 이어지는지 연결 고리가 있는가?" },
      { "id": "a3", "name": "[지표] 성공 기준: 이번 리서치가 성공적으로 끝났다고 판단할 수 있는 기준이 있는가?" },
      { "id": "a4", "name": "[공유] 확산 계획: 조사 결과를 팀 내/외부에 어떻게 공유하고 자산화(Archive)할 것인지 계획이 있는가?" },
      { "id": "a5", "name": "[한계] 조사의 한계점: 이번 조사에서 알 수 없는 것(Limitation)을 미리 명시하여 기대 수준을 조절했는가?" }
    ]
  }
]`;

function parseJsonSafe(str, fallback) {
  try {
    const v = JSON.parse(str);
    return v != null ? v : fallback;
  } catch {
    return fallback;
  }
}

/** 결과 → 시트 붙여넣기용 (한 셀에 Pass 또는 Fail+이유) */
function buildSheetCopyText(result) {
  if (!result?.criteria) return "";
  const rows = [];
  for (const c of result.criteria) {
    for (const s of c.sub_items || []) {
      const pf = s.pass_fail ? "Pass" : "Fail";
      const reason = !s.pass_fail && s.reason ? String(s.reason).trim() : "";
      rows.push(reason ? `${pf} ${reason}` : pf);
    }
  }
  return rows.join("\n");
}

export default function DevEvaluatorPage() {
  const navigate = useNavigate();
  const [evaluationType, setEvaluationType] = useState("plan_quality");
  const [artifactType, setArtifactType] = useState("plan");
  const [stage, setStage] = useState("final");
  const [payloadMode, setPayloadMode] = useState("content"); // content | stage_text | artifact_id
  const [contentText, setContentText] = useState("");
  const [stageText, setStageText] = useState("");
  const [artifactId, setArtifactId] = useState("");
  const [criteriaJson, setCriteriaJson] = useState(DEFAULT_CRITERIA_JSON);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [lastCriteria, setLastCriteria] = useState(null);
  const [criteriaPreviewOpen, setCriteriaPreviewOpen] = useState(false);

  const buildPayload = useCallback(() => {
    if (payloadMode === "artifact_id" && artifactId.trim()) {
      return { artifact_id: parseInt(artifactId.trim(), 10) };
    }
    if (payloadMode === "content") {
      return { content: contentText };
    }
    // stage_text: messages_text + ledger_cards (JSON) 합쳐서 messages_text로 보내거나, 단일 텍스트로
    const parsed = parseJsonSafe(stageText, null);
    if (Array.isArray(parsed)) {
      return { messages_text: "(대화 생략)", ledger_cards: parsed };
    }
    if (parsed && typeof parsed === "object" && (parsed.messages_text != null || parsed.ledger_cards != null)) {
      return { messages_text: parsed.messages_text || "", ledger_cards: parsed.ledger_cards || [] };
    }
    return { messages_text: stageText, ledger_cards: [] };
  }, [payloadMode, contentText, stageText, artifactId]);

  const handleRun = async () => {
    setError(null);
    setResult(null);
    const isCardMode = evaluationType === "card_appropriateness";
    let criteria;
    if (isCardMode) {
      criteria = STAGE_CARD_CRITERIA[stage] || [];
      if (criteria.length === 0) {
        setError("카드 적절성 평가는 1~5단계만 선택 가능합니다.");
        return;
      }
    } else {
      const allCriteria = parseJsonSafe(criteriaJson, []);
      if (!Array.isArray(allCriteria) || allCriteria.length === 0) {
        setError("평가 기준(criteria)을 올바른 JSON 배열로 입력해주세요.");
        return;
      }
      const allowedIds = STAGE_CRITERIA_IDS[stage];
      criteria =
        allowedIds == null
          ? allCriteria
          : allCriteria.filter((c) => allowedIds.includes(c.id));
      if (criteria.length === 0) {
        setError(`선택한 단계(${stage}단계)에 해당하는 평가 항목이 없습니다. 기준 JSON에 id가 ${(STAGE_CRITERIA_IDS[stage] || []).join(", ")} 인 항목을 넣어주세요.`);
        return;
      }
    }
    const payload = buildPayload();
    setLoading(true);
    try {
      const data = await devEvaluate({
        artifact_type: artifactType,
        stage,
        payload,
        criteria,
        ...(isCardMode ? { evaluation_mode: "card_appropriateness" } : {}),
      });
      setLastCriteria(criteria);
      setResult(data);
    } catch (e) {
      setError(e.message || "평가 요청 실패");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dev-evaluator-page">
      <header className="dev-evaluator-header">
        <button type="button" className="dev-evaluator-back" onClick={() => navigate(-1)}>
          ← 뒤로
        </button>
        <h1>산출물 평가 테스트기 (개발용)</h1>
        <p className="dev-evaluator-subtitle">Plan / Survey / Guideline / Report 범용 판때기</p>
      </header>

      <section className="dev-evaluator-section">
        <h2>1. 평가 대상</h2>
        <div className="dev-evaluator-row">
          <label>
            평가 유형
            <select
              value={evaluationType}
              onChange={(e) => {
                const v = e.target.value;
                setEvaluationType(v);
                if (v === "card_appropriateness") {
                if (stage === "final") setStage(1);
                setPayloadMode("content");
              }
              }}
            >
              {EVALUATION_TYPES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label>
            산출물 유형
            <select value={artifactType} onChange={(e) => setArtifactType(e.target.value)}>
              {ARTIFACT_TYPES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label>
            단계
            <select
              value={stage}
              onChange={(e) => setStage(e.target.value === "final" ? "final" : Number(e.target.value))}
            >
              {STAGES.filter((o) => evaluationType !== "card_appropriateness" || o.value !== "final").map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>
        {evaluationType === "card_appropriateness" && (
          <p className="dev-evaluator-stage-hint">
            이 단계에서 사용할 평가: 카드 적절성 — &quot;{STAGE_CARD_CRITERIA[stage]?.[0]?.name ?? ""}&quot;
          </p>
        )}
        {evaluationType === "plan_quality" && (() => {
          if (stage === "final") {
            return (
              <p className="dev-evaluator-stage-hint">
                이 단계에서 사용할 평가 항목: 전체
              </p>
            );
          }
          const list = parseJsonSafe(criteriaJson, []);
          const allowedIds = STAGE_CRITERIA_IDS[stage];
          const forStage = Array.isArray(list) && allowedIds
            ? list.filter((c) => allowedIds.includes(c.id))
            : [];
          const names = forStage.map((c) => c.name || c.id);
          if (names.length === 0) return null;
          return (
            <p className="dev-evaluator-stage-hint">
              이 단계에서 사용할 평가 항목: {names.join(" / ")}
            </p>
          );
        })()}
        <div className="dev-evaluator-row">
          <label>
            입력 방식
            <select value={payloadMode} onChange={(e) => setPayloadMode(e.target.value)}>
              <option value="content">본문 텍스트 (content)</option>
              <option value="stage_text">1~5단계 텍스트/JSON (messages_text + ledger_cards)</option>
              <option value="artifact_id">계획서 artifact_id (최종만)</option>
            </select>
          </label>
        </div>
        {evaluationType === "card_appropriateness" && (
          <p className="dev-evaluator-stage-hint">
            <strong>UI에 보이는 후보 카드 목록을 그대로 붙여넣으세요.</strong> (예: &quot;후보 항목이 생성됐어요. 필요한 것만 선택해 주세요.&quot; + 카드 제목, 타입, 본문, 선택하기…) JSON으로 바꿀 필요 없습니다.
          </p>
        )}
        {payloadMode === "content" && (
          <textarea
            className="dev-evaluator-textarea"
            placeholder={
              evaluationType === "card_appropriateness"
                ? "예: 후보 항목이 생성됐어요. 필요한 것만 선택해 주세요.\n\n베타 서비스 사용자 행태 분석\nproject_context\n네이버페이 증권 PC 웹사이트의 베타 버전에서...\n선택하기\n\n신규 기능 검증 범위 설정\nscope_item\n..."
                : "평가할 산출물 본문을 붙여넣으세요 (마크다운 등)"
            }
            value={contentText}
            onChange={(e) => setContentText(e.target.value)}
            rows={10}
          />
        )}
        {payloadMode === "stage_text" && (
          <textarea
            className="dev-evaluator-textarea"
            placeholder='1~5단계: messages_text 문자열 또는 { "messages_text": "...", "ledger_cards": [...] } JSON'
            value={stageText}
            onChange={(e) => setStageText(e.target.value)}
            rows={8}
          />
        )}
        {payloadMode === "artifact_id" && (
          <input
            type="text"
            className="dev-evaluator-input"
            placeholder="artifact ID (숫자)"
            value={artifactId}
            onChange={(e) => setArtifactId(e.target.value)}
          />
        )}
      </section>

      <section className="dev-evaluator-section">
        <h2>2. 평가 기준</h2>
        {evaluationType === "card_appropriateness" ? (
          <div className="dev-evaluator-card-criteria-block">
            <p className="dev-evaluator-hint">이 단계(카드 적절성)에서는 아래 기준이 자동 적용됩니다. 수정 불가.</p>
            <ul className="dev-evaluator-preview-list">
              {(STAGE_CARD_CRITERIA[stage] || []).map((c) => (
                <li key={c.id || c.name}>
                  <strong>{c.name || c.id}</strong>
                  <ul>
                    {(c.sub_items || []).map((s) => (
                      <li key={s.id}>{s.name || s.id}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            <div className="dev-evaluator-criteria-preview">
              <button
                type="button"
                className="dev-evaluator-preview-toggle"
                onClick={() => setCriteriaPreviewOpen((o) => !o)}
                aria-expanded={criteriaPreviewOpen}
              >
                {criteriaPreviewOpen ? "▼" : "▶"} 현재 기준 미리보기 (질문만 보기)
              </button>
              {criteriaPreviewOpen && (() => {
                const list = parseJsonSafe(criteriaJson, []);
                if (!Array.isArray(list) || list.length === 0) {
                  return <p className="dev-evaluator-preview-empty">유효한 JSON을 입력하면 여기에 표시됩니다.</p>;
                }
                return (
                  <ul className="dev-evaluator-preview-list">
                    {list.map((c) => (
                      <li key={c.id || c.name}>
                        <strong>{c.name || c.id}</strong>
                        <ul>
                          {(c.sub_items || []).map((s) => (
                            <li key={s.id}>{s.name || s.id}</li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
            <p className="dev-evaluator-hint">아래에서 JSON을 수정해도 됩니다. 바꾼 뒤 미리보기를 다시 열어 확인하세요.</p>
            <textarea
              className="dev-evaluator-textarea dev-evaluator-criteria"
              value={criteriaJson}
              onChange={(e) => setCriteriaJson(e.target.value)}
              rows={10}
              spellCheck={false}
              placeholder="평가 기준 JSON 배열..."
            />
          </>
        )}
      </section>

      <section className="dev-evaluator-section">
        <button type="button" className="dev-evaluator-run" onClick={handleRun} disabled={loading}>
          {loading ? "평가 중…" : "평가 실행"}
        </button>
        {error && <div className="dev-evaluator-error">{error}</div>}
      </section>

      {result && (
        <section className="dev-evaluator-section dev-evaluator-result">
          <h2>3. 결과</h2>
          <div className="dev-evaluator-final-score">
            최종 점수: <strong>{result.final_score}</strong>점
          </div>
          <ul className="dev-evaluator-criteria-list">
            {(result.criteria || []).map((c) => {
              const def = (lastCriteria || []).find((d) => d.id === c.id);
              return (
                <li key={c.id} className="dev-evaluator-criterion">
                  <div className="dev-evaluator-criterion-head">
                    <span className="dev-evaluator-criterion-name">{def ? def.name : c.id}</span>
                    <span className="dev-evaluator-criterion-score">{c.score}점</span>
                  </div>
                  <ul className="dev-evaluator-sub-list">
                    {(c.sub_items || []).map((s) => {
                      const subDef = (def?.sub_items || []).find((d) => d.id === s.id);
                      const questionText = subDef ? (subDef.name || subDef.description || s.id) : s.id;
                      return (
                        <li key={s.id} className={s.pass_fail ? "pass" : "fail"}>
                          <span className="dev-evaluator-sub-label">{s.pass_fail ? "Pass" : "Fail"}</span>
                          <span className="dev-evaluator-sub-question">{questionText}</span>
                          {!s.pass_fail && s.reason && (
                            <span className="dev-evaluator-sub-reason">→ {s.reason}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </ul>
          <div className="dev-evaluator-copy-block">
            <div className="dev-evaluator-copy-header">
              <span>시트 붙여넣기용 (탭 구분)</span>
              <button
                type="button"
                className="dev-evaluator-copy-btn"
                onClick={() => {
                  const tsv = buildSheetCopyText(result);
                  navigator.clipboard?.writeText(tsv).then(() => alert("복사되었습니다. 시트에 붙여넣기(Ctrl+V) 하세요.")).catch(() => alert("복사 실패"));
                }}
              >
                복사
              </button>
            </div>
            <pre className="dev-evaluator-copy-pre">{buildSheetCopyText(result)}</pre>
          </div>
        </section>
      )}
    </div>
  );
}
